import type { ActivityEvent, OptionPeriodView, Suspense, SuspenseStatus, SuspenseType } from '../api/trackerFunctions';

export interface TrackerFilters {
  contract?: string;
  type?: SuspenseType | '';
  owner?: string;
  status?: SuspenseStatus | '';
}

const dayKey = (date: Date): number => Date.UTC(date.getUTCFullYear(), date.getUTCMonth(), date.getUTCDate());

export function sortSuspenses(suspenses: Suspense[]): Suspense[] {
  return [...suspenses].sort((a, b) => {
    const byDate = a.dueDate.localeCompare(b.dueDate);
    return byDate || a.title.localeCompare(b.title);
  });
}

/** Keeps undated legacy rows behind authoritative dated events without inventing a timestamp. */
export function sortActivityEventsNewestFirst(events: ActivityEvent[]): ActivityEvent[] {
  const timestamp = (event: ActivityEvent) => event.occurredAt ? Date.parse(event.occurredAt) : Number.NaN;
  return [...events].sort((a, b) => {
    const aTime = timestamp(a);
    const bTime = timestamp(b);
    const aDated = Number.isFinite(aTime);
    const bDated = Number.isFinite(bTime);
    if (aDated && bDated) return bTime - aTime || a.eventId.localeCompare(b.eventId);
    if (aDated) return -1;
    if (bDated) return 1;
    return a.eventId.localeCompare(b.eventId);
  });
}

/**
 * Keeps reminder placement as a presentation concern: backend urgency and
 * eligibility remain authoritative, while this groups the server projection
 * beneath its target option period.
 */
export function groupRemindersByOptionPeriod(
  optionPeriods: OptionPeriodView[],
  suspenses: Suspense[],
): Record<string, Suspense[]> {
  return optionPeriods.reduce<Record<string, Suspense[]>>((grouped, period) => {
    const linked = period.reminders.length > 0
      ? period.reminders
      : suspenses.filter(item => item.optionPeriodId === period.optionPeriodId);
    grouped[period.optionPeriodId] = sortSuspenses(linked);
    return grouped;
  }, {});
}

export function filterSuspenses(suspenses: Suspense[], filters: TrackerFilters = {}): Suspense[] {
  return sortSuspenses(suspenses.filter(item => {
    if (filters.contract && item.contractNumber !== filters.contract) return false;
    if (filters.type && item.type !== filters.type) return false;
    if (filters.owner && item.owner !== filters.owner) return false;
    if (filters.status && item.status !== filters.status) return false;
    return true;
  }));
}

export function portfolioStats(suspenses: Suspense[], today = new Date()): {
  overdue: number;
  dueThisWeek: number;
  dueThisMonth: number;
  contracts: number;
} {
  const start = dayKey(today);
  const daysUntil = (dueDate: string) => (dayKey(new Date(`${dueDate}T00:00:00Z`)) - start) / 86_400_000;
  const active = suspenses.filter(item => item.status !== 'complete');
  return {
    overdue: active.filter(item => item.status === 'overdue').length,
    dueThisWeek: active.filter(item => { const days = daysUntil(item.dueDate); return days >= 0 && days <= 7; }).length,
    dueThisMonth: active.filter(item => { const days = daysUntil(item.dueDate); return days >= 0 && days <= 30; }).length,
    contracts: new Set(suspenses.map(item => item.contractNumber).filter(Boolean)).size,
  };
}
